import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Shield, Mail, Cloud, Users, Lock, Server } from 'lucide-react';
import Button from '../common/Button';
import DemoRequest from './DemoRequest';
import ProductScreenshots from './ProductScreenshots';

const Scale365Section = () => {
  const { t } = useTranslatedContent('scale365');

  // Get features from translation and ensure it's an array
  const features = t('features.items', { returnObjects: true }) || [];

  // Map of icons to use for each feature
  const featureIcons = {
    0: Shield, // Unified Access Control
    1: Mail,   // Enterprise Email
    2: Cloud,  // Secure Cloud Storage
    3: Users,  // Identity Management
    4: Lock,   // Advanced Security
    5: Server  // High Availability
  };

  return (
    <div id="scale365" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-400/20 backdrop-blur-sm mb-8">
            <Shield className="h-5 w-5 text-purple-400 mr-2" />
            <span className="text-purple-200 font-medium">{t('hero.badge')}</span>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {Array.isArray(features) && features.map((feature, index) => {
            const Icon = featureIcons[index as keyof typeof featureIcons];
            return (
              <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-purple-100">{feature.description}</p>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-purple-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-indigo-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>

        {/* Screenshots Section */}
        <div className="mb-20">
          <ProductScreenshots />
        </div>

        {/* Demo Request Section */}
        <div className="max-w-3xl mx-auto">
          <DemoRequest />
        </div>
      </div>
    </div>
  );
};

export default Scale365Section;